import {
    Component, ChangeDetectorRef, ChangeDetectionStrategy, Input, HostListener, ElementRef
} from '@angular/core';
import { SeConstraintNotifierService } from '@se/constraint';
import { DataBinding, Logger } from '@se/se.ui.html5';
import { AbstractControlComponent } from '../abstract-control';
import { CpEntity, CpGridDefaultGroupsControl, ICpEntity } from '../../cp-configuration/view-model';
import { CpDatasourceService } from '../../cp-datasource.service';
import { IDataSourceItem, IDataSource, IDataset, IDatasetControl, IDataSourceExtendedItem } from '@se/cp.common';
import { CpModelService } from '../../cp-model.service';
import { CpDatasetService } from "../../cp-dataset-service";
import { TranslateService } from '@ngx-translate/core';
import { ListInfoClientService } from './list-info-client.service';
import { IListColumn, IListInfo } from './list-info';


@Component({
    selector: 'cp-grid-default-groups',
    templateUrl: './grid-default-groups.component.html',
    styleUrls: ['./grid-default-groups.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CpGridDefaultGroupsComponent extends AbstractControlComponent {
    @Input() onlyIcon : boolean = false;

    control! : CpGridDefaultGroupsControl;
    dataset: IDataset;
    groupsX : string = "0";
    groupsY : string = "0";
    _groups: IDataSourceExtendedItem[] = [];
    private groupListFieldKey: string = '';
    private itemsGroups : IDataSource = [];
    isButtonDisabled : boolean = false;
    iconTooltip : string = "grid-default-groups-button_tooltip";
    showGroups : boolean = false;

    @HostListener('document:click', ['$event']) onClickOutside (event : MouseEvent) {
        if (this.showGroups && !this.elemRef.nativeElement.contains(event.target)) {
            this.showGroups = false;
        }
    }

    constructor (
        private elemRef : ElementRef,
        protected cdr : ChangeDetectorRef,
        protected constraintNotifierService : SeConstraintNotifierService,
        private datasetService : CpDatasetService,
        private datasourceService : CpDatasourceService,
        protected logger : Logger,
        private modelService : CpModelService,
        private translate: TranslateService,
        private listInfoClient: ListInfoClientService
    ) {
        super(cdr, constraintNotifierService, logger);

    }

    // #region overrides
    protected onModelChange (_ : unknown) : boolean {
        return false;
    }

    // #endregion

    init(): void {
        this.setGroupListKey();
        this.populateGridDefaultGroupsDatasource();
        this.iconTooltip = this.translate.instant(this.control.iconTooltip || "AddDefaultGroup"); // default dal layaut o da codice
        if (!this._groups || this._groups.length === 0) {
            this.isButtonDisabled = true;
            this.iconTooltip = this.translate.instant("NoDataToImport");
        }
        this.dataset = this.datasetService.getNode(this.control.dataBinding);      
    }

    // #region Callbacks
    async onClick (event : MouseEvent) {
        const windowWidth : number = window.innerWidth;
        if (this._groups.length === 1) {
            this.addGroupItems(this._groups[0]);
        } else {
            this.groupsX = windowWidth <= 800 ? (event.x - 316) + 'px' : (event.x - 516) + 'px';
            this.groupsY = (event.y - 10) + 'px';
            this.showGroups = true;
        }
    }

    onCloseGroups () {
        this.showGroups = false;
    }

    addGroupItems(group: IDataSourceItem) {
        const groupItems : Array<IDataSourceItem> = this.extractGroupItems(group.Id);
        const parentEntity : ICpEntity | undefined = this.modelService.getEntity(this.control.dataBinding);
        if (!parentEntity) return; //gestire come errore?
        const parentPath = new DataBinding(this.control.dataBinding.simple);
        groupItems.forEach((item : IDataSourceItem) => this.createAdvancedEntity(parentEntity, parentPath, item));

        this.modelService.setValue(parentPath, parentEntity, {
            emitEvent: true,
            force: true
        });
        this.edited.emit();
        this.showGroups = false;
    }

    // #endregion


    // #region privates

    private populateGridDefaultGroupsDatasource () {
        this._groups = this.setDataSourceAndOrder(this.control.idGroupList);
        this.itemsGroups = this.setDataSourceAndOrder(this.control.idGroupItemList);
    }

    private setDataSourceAndOrder (idDataSource : string) : IDataSourceExtendedItem[] {
        let currentDataSource : IDataSourceExtendedItem[] = this.getDatasource(idDataSource);
        return currentDataSource.sort(this.orderDataSource);
    };

    private getDatasource (idDataSource : string) : IDataSourceExtendedItem[] {
        let currentDataSource : IDataSourceExtendedItem[] = [];
        if (idDataSource) {
            currentDataSource = <IDataSourceExtendedItem[]> this.datasourceService.getById(idDataSource);
        }
        return currentDataSource;
    }

    private orderDataSource(itemA: IDataSourceExtendedItem, itemB: IDataSourceExtendedItem): number {
        const orderItemA: number = (itemA.Order ?? null) as number;
        const orderItemB: number = (itemB.Order ?? null) as number;

        if (orderItemA === orderItemB === null) {
            return 0;
        }
        if (orderItemA === null) {
            return -1;
        }

        if (orderItemB === null) {
            return 1
        }

        return (orderItemA - orderItemB);
    }

    private extractGroupItems(parentId: string): Array<any> {
        return this.itemsGroups.filter((item: any) => item[this.groupListFieldKey] === parentId);
    }

    private createAdvancedEntity (parentEntity : ICpEntity, parentPath : DataBinding, item : IDataSourceItem) {
        const newEntity = this.modelService.makeEntityFromDataset(parentPath, parentEntity);
   
        if (!newEntity) {
            return; //gestire come errore?
        }
        parentEntity.addEntityToList(new DataBinding(), newEntity);
        this.extractAndSetPropertiesValue(item, newEntity);
    }

    private extractAndSetPropertiesValue (item : IDataSourceItem, entity : ICpEntity) {
        for (const [controlName, datasourceItemId] of Object.entries(item)) {
            const datasetControl = this.datasetControlByName(controlName);
            if (!datasetControl) {
                continue;
            }
            if (datasetControl.dataType === 'Guid' || datasetControl.dataType === 'GuidArray') {
                if (!datasetControl.itemsSource) {
                    continue;
                }
                const datasourceEntity = this.prepareComplexProperty(entity, datasetControl, datasourceItemId as string);
                entity?.setPropertyValue(controlName, datasourceEntity, true);
            } else {
                entity?.setPropertyValue(controlName, datasourceItemId, true);
            }
        }
    }

    private datasetControlByName (name : string) : IDatasetControl {
        return this.dataset.controls.find(el => el.name === name);
    }

    private prepareComplexProperty (entity : ICpEntity, datasetControl : IDatasetControl, datasourceId : string) : ICpEntity {
        const datasource : Array<IDataSourceExtendedItem> = this.getDatasource(datasetControl.itemsSource);
        const datasourceItem = datasource.find(el => el.Id === datasourceId);
        return this.modelService.makeEntity<CpEntity>(datasourceItem as any, entity.path) as ICpEntity;
    }

    private setGroupListKey() {
        let isPreview = this.modelService.getEntity(new DataBinding())?.getPropertyValue('IsPreview') ?? false;

        if (!isPreview) {
            this.listInfoClient.getListInfo(this.control.idGroupItemList).subscribe((item: IListInfo) => {
                if (item && item.columns && item.columns.length > 0) {
                    const extractedCol = item.columns.find((elem: IListColumn) => elem.reference == this.control.idGroupList);
                    if (extractedCol) {
                        this.groupListFieldKey = extractedCol.name;
                    }
                }
            });
        }
    }

    // #endregion
}



